import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Text, Button } from '@bufferapp/ui';
import { grayDark } from '@bufferapp/ui/style/colors';
import { Checklist } from '~/shared-components';
import { actions as modalActions } from '~/modals/reducer';
import { SEGMENT_NAMES } from '~/constants';

import { AppDispatch, useAppDispatch } from '~/store';
import { getURL } from '~/server/formatters';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { useAccount } from '../../../accountContext';

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-left: 48px;
  align-items: stretch;
`;

const Content = styled.div`
  margin-top: 43px;
  flex: 1;
  max-width: 378px;
`;

const Image = styled.img`
  max-width: 50%;
  max-height: 89.5vh;
  object-fit: cover;
  object-position: 0 0;
  flex: 2;
  margin-left: 100px;
  overflow: hidden;

  @media (min-width: 1500px) {
    flex: 1.5;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  #collab-start-trial-cta {
    height: 40px;
    margin-right: 8px;
  }

  > p {
    font-size: 12px;
    color: ${grayDark};
  }
`;

const navigateToMigrationHub = (cta) => {
  const migrationHubURL = cta
    ? `${getURL.getMigrationHubURL()}?${cta}`
    : `${getURL.getMigrationHubURL()}`;

  window.location.assign(migrationHubURL);
};

export function getUserChannelLimit(user) {
  return (
    user?.account?.currentOrganization?.channelLimit ||
    user?.account?.currentOrganization?.limits?.channels
  );
}

export function getUserUnlockedChannelCount(user) {
  const unlockedChannels =
    user?.account?.currentOrganization?.channels?.filter(
      (channel) => !channel?.isLocked
    ) || [];

  return unlockedChannels.length;
}

export function isAtPlanLimit(user) {
  const channelLimit = getUserChannelLimit(user);
  const channelCount = getUserUnlockedChannelCount(user);

  return channelCount >= channelLimit;
}

const ApprovalsUpsell = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const user = useAccount();
  const { isOneBufferOrganization } = user?.account?.currentOrganization || {};
  const canStartTrial =
    user?.account?.currentOrganization?.billing?.canStartTrial;

  const commonTrackingProps =
    user?.account?.currentOrganization?.commonTrackingProperties || null;

  const onUpsellClick = ({ dispatch }: { dispatch: AppDispatch }): void => {
    if (isOneBufferOrganization) {
      BufferTracker.ctaClicked({
        organizationId: user?.account?.currentOrganization?.id as string,
        clientName: 'publishWeb',
        product: 'publish',
        cta: 'publish-approvals-queueTabs-upgrade-1',
        ctaName: 'publish-approvals-queueTabs-upgrade-1',
        upgradePathName: 'approvals-upgrade',
        ...commonTrackingProps,
      });
      const { MODALS, actions } = window?.appshell || {};
      actions.openModal(MODALS.planSelector, {
        cta: SEGMENT_NAMES.APPROVALS_UPSELL,
        upgradePathName: 'approvals-upgrade',
      });
    } else {
      navigateToMigrationHub(SEGMENT_NAMES.APPROVALS_UPSELL_TO_MIGRATION_HUB);
    }

    dispatch(modalActions.hideTrialCollaborationModal());
  };

  const onTrialClick = (): void => {
    BufferTracker.ctaClicked({
      organizationId: user?.account?.currentOrganization?.id as string,
      clientName: 'publishWeb',
      product: 'publish',
      cta: 'publish-approvals-queueTabs-trial-1',
      ctaName: 'publish-approvals-queueTabs-trial-1',
      upgradePathName: 'approvals-trial',
      ...commonTrackingProps,
    });

    const { MODALS, actions } = window?.appshell || {};
    actions.openModal(MODALS.startTrial, {
      cta: 'publish-approvals-queueTabs-trial-1',
      upgradePathName: 'approvals-trial',
    });
  };

  useEffect(() => {
    const ctaButton = canStartTrial ? 'trial' : 'upgrade';
    BufferTracker.ctaViewed({
      organizationId: user?.account?.currentOrganization?.id || null,
      ctaName: `publish-approvals-queueTabs-${ctaButton}-1`,
      currentChannelQuantity:
        user?.account?.currentOrganization?.channels?.length || 0,
      // TODO: replace with isAtPlanLimit coming from backend
      isAtPlanLimit: isAtPlanLimit(user),
      upgradePathName: `approvals-${ctaButton}`,
      product: 'publish',
      ...commonTrackingProps,
    });
  }, [canStartTrial, user, commonTrackingProps]);

  return (
    <EmptyStateContainer>
      <Content>
        <Text type="h1">Collaboration made easy</Text>
        <Text type="p">
          Say goodbye to the hassle of managing multiple social media channels
          with multiple team mates. With our collaboration features, you can:
        </Text>
        <Checklist
          items={[
            'Choose who can post on each of your social media channels',
            'Review posts for quality and brand before hitting publish',
            'Collaborate on ideas',
            'Stay on top of performance with automated reports',
          ]}
        />
        <div style={{ alignSelf: 'flex-end', paddingTop: '30px' }}>
          {canStartTrial && (
            <CTAWrapper>
              <Button
                data-testid="approvals-start-trial"
                type="primary"
                label="Try it free for 14 days"
                onClick={(): void => {
                  onTrialClick();
                }}
              />
              <Text type="p">No credit card required</Text>
            </CTAWrapper>
          )}
          {!canStartTrial && (
            <Button
              data-testid="approvals-upsell"
              type="primary"
              label="Discover the Team Pack"
              onClick={(): void => {
                onUpsellClick({ dispatch });
              }}
            />
          )}
        </div>
      </Content>
      <Image
        src="https://buffer-ui.s3.amazonaws.com/upgrade-paths/publish/approvals-upsell.jpg"
        alt="Example of the approvals premium feature."
      />
    </EmptyStateContainer>
  );
};

export default ApprovalsUpsell;
